<template>
  <div class="gl-page">

    <!-- Page Header -->
    <div class="gl-page-header">
      <div class="gl-page-title-row">
        <div>
          <div class="gl-breadcrumb">
            <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
            <span class="gl-breadcrumb-sep">›</span>
            <router-link to="/owner/subscription" class="gl-breadcrumb-link">Subscription</router-link>
            <span class="gl-breadcrumb-sep">›</span>
            <span class="gl-breadcrumb-cur">Billing History</span>
          </div>
          <h1 class="gl-page-title">Billing History</h1>
          <p class="gl-page-sub">All your invoices and payment records</p>
        </div>
        <a href="mailto:billing@getlokal.my" class="gl-btn-contact">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          Contact Billing
        </a>
      </div>
    </div>

    <!-- Stats Strip -->
    <div class="gl-stats-strip">
      <div class="gl-stat-card">
        <template v-if="loading">
          <div class="gl-shimmer" style="height:44px;width:44px;border-radius:11px;flex-shrink:0;"></div>
          <div style="flex:1;">
            <div class="gl-shimmer" style="height:11px;width:80px;border-radius:6px;margin-bottom:8px;"></div>
            <div class="gl-shimmer" style="height:20px;width:100px;border-radius:6px;"></div>
          </div>
        </template>
        <template v-else>
          <div class="gl-stat-icon gl-icon-teal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Current Plan</div>
            <div class="gl-stat-value">{{ currentPlan }}</div>
          </div>
        </template>
      </div>

      <div class="gl-stat-card">
        <template v-if="loading">
          <div class="gl-shimmer" style="height:44px;width:44px;border-radius:11px;flex-shrink:0;"></div>
          <div style="flex:1;">
            <div class="gl-shimmer" style="height:11px;width:80px;border-radius:6px;margin-bottom:8px;"></div>
            <div class="gl-shimmer" style="height:20px;width:100px;border-radius:6px;"></div>
          </div>
        </template>
        <template v-else>
          <div class="gl-stat-icon gl-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Next Billing Date</div>
            <div class="gl-stat-value">{{ nextBillingDate }}</div>
          </div>
        </template>
      </div>

      <div class="gl-stat-card">
        <template v-if="loading">
          <div class="gl-shimmer" style="height:44px;width:44px;border-radius:11px;flex-shrink:0;"></div>
          <div style="flex:1;">
            <div class="gl-shimmer" style="height:11px;width:80px;border-radius:6px;margin-bottom:8px;"></div>
            <div class="gl-shimmer" style="height:20px;width:100px;border-radius:6px;"></div>
          </div>
        </template>
        <template v-else>
          <div class="gl-stat-icon" :class="totalPaid > 0 ? 'gl-icon-green' : 'gl-icon-muted'">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Paid</div>
            <div class="gl-stat-value" :class="totalPaid > 0 ? 'gl-val-teal' : 'gl-val-muted'">
              RM {{ formatCurrency(totalPaid) }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Invoices Table Card -->
    <div class="gl-table-card">
      <div class="gl-table-header">
        <div>
          <h2 class="gl-section-title">Invoices</h2>
          <p class="gl-section-sub">
            <template v-if="loading">Loading…</template>
            <template v-else-if="filteredInvoices.length">{{ pagination.total || invoices.length }} records found</template>
            <template v-else>No invoices yet</template>
          </p>
        </div>
        <div class="gl-table-controls">
          <div class="gl-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input v-model="searchQuery" class="gl-search-input" placeholder="Search invoices…" />
          </div>
          <div class="gl-select-wrap">
            <select v-model="statusFilter" class="gl-filter-select">
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <svg class="gl-select-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>

      <!-- Scrollable invoice table wrap -->
      <div class="gl-inv-wrap">

      <!-- Skeleton -->
      <div v-if="loading">
        <div class="gl-inv-thead">
          <span>Invoice #</span><span>Date</span><span>Description</span><span class="gl-col-right">Amount</span><span>Status</span>
        </div>
        <div v-for="i in 4" :key="i" class="gl-inv-row gl-inv-row-skel">
          <div class="gl-shimmer" style="height:13px;width:130px;border-radius:6px;"></div>
          <div class="gl-shimmer" style="height:13px;width:90px;border-radius:6px;"></div>
          <div class="gl-shimmer" style="height:13px;width:200px;border-radius:6px;"></div>
          <div style="margin-left:auto;display:flex;flex-direction:column;align-items:flex-end;gap:5px;">
            <div class="gl-shimmer" style="height:14px;width:90px;border-radius:6px;"></div>
            <div class="gl-shimmer" style="height:11px;width:140px;border-radius:6px;"></div>
          </div>
          <div class="gl-shimmer" style="height:24px;width:64px;border-radius:20px;"></div>
        </div>
      </div>

      <!-- Table -->
      <template v-else-if="filteredInvoices.length">
        <div class="gl-inv-thead">
          <span style="text-align:center;">Invoice #</span>
          <span style="text-align:center;">Date</span>
          <span>Description</span>
          <span class="gl-col-right">Amount</span>
          <span style="text-align:center;">Status</span>
          <span></span>
        </div>
        <div
          v-for="invoice in filteredInvoices"
          :key="invoice.id"
          class="gl-inv-row"
          :class="`gl-row-${invoice.status}`"
        >
          <span class="gl-inv-num">{{ invoice.invoice_number }}</span>
          <span class="gl-inv-date">{{ formatDate(invoice.created_at) }}</span>
          <span class="gl-inv-desc">{{ invoice.description }}</span>
          <div class="gl-inv-amount">
            <span class="gl-inv-total">RM {{ formatCurrency(invoice.total) }}</span>
            <span class="gl-inv-breakdown">
              Subtotal RM {{ formatCurrency(invoice.subtotal) }}&nbsp;&nbsp;+&nbsp;&nbsp;Tax RM {{ formatCurrency(invoice.tax) }}
            </span>
          </div>
          <span :class="['gl-badge', `gl-badge-${invoice.status}`]" style="display:block;text-align:center;">{{ capitalize(invoice.status) }}</span>
          <button class="gl-dl-btn" @click="downloadInvoice(invoice)" title="Download">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
        </div>
      </template>

      <!-- Empty -->
      <div v-else class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        </div>
        <p class="gl-empty-title">{{ searchQuery || statusFilter ? 'No matching invoices' : 'No invoices yet' }}</p>
        <p class="gl-empty-sub">{{ searchQuery || statusFilter ? 'Try adjusting your filters.' : 'Your billing history will appear here once you have an active subscription.' }}</p>
      </div>

      </div><!-- /gl-inv-wrap -->

      <!-- Pagination -->
      <div v-if="!loading && pagination.lastPage > 1" class="gl-pagination">
        <button class="gl-pg-btn" :disabled="pagination.currentPage === 1" @click="loadInvoices(pagination.currentPage - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <template v-for="page in visiblePages" :key="page">
          <span v-if="page === '...'" class="gl-pg-ellipsis">…</span>
          <button v-else class="gl-pg-btn" :class="{ 'gl-pg-active': page === pagination.currentPage }" @click="loadInvoices(page)">{{ page }}</button>
        </template>
        <button class="gl-pg-btn" :disabled="pagination.currentPage === pagination.lastPage" @click="loadInvoices(pagination.currentPage + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>

    <!-- Payment Method -->
    <div class="gl-payment-card">
      <div class="gl-payment-left">
        <div class="gl-payment-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
        </div>
        <div>
          <h3 class="gl-payment-title">Payment Method</h3>
          <p class="gl-payment-sub">Automated billing management is coming soon. For any billing queries or changes, please contact our team directly.</p>
        </div>
      </div>
      <a href="mailto:billing@getlokal.my" class="gl-btn-contact-outline">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
        billing@getlokal.my
      </a>
    </div>

  </div>
</template>

<script>
import { subscriptionApi } from '@/services/subscriptionApi';

export default {
  name: 'BillingHistory',
  data() {
    return {
      loading: true,
      invoices: [],
      subscription: null,
      searchQuery: '',
      statusFilter: '',
      pagination: { currentPage: 1, lastPage: 1, total: 0 }
    };
  },
  computed: {
    currentPlan() { return this.subscription?.plan?.name || 'No Active Plan'; },
    nextBillingDate() {
      if (!this.subscription?.current_period_end) return '—';
      return this.formatDate(this.subscription.current_period_end);
    },
    totalPaid() {
      return this.invoices
        .filter(inv => inv.is_paid || inv.status === 'paid')
        .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);
    },
    filteredInvoices() {
      const q = this.searchQuery.toLowerCase();
      return this.invoices.filter(inv => {
        const matchSearch = !q || inv.invoice_number?.toLowerCase().includes(q) || inv.description?.toLowerCase().includes(q);
        const matchStatus = !this.statusFilter || inv.status === this.statusFilter;
        return matchSearch && matchStatus;
      });
    },
    visiblePages() {
      const cur = this.pagination.currentPage;
      const last = this.pagination.lastPage;
      if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
      if (cur <= 4) return [1, 2, 3, 4, 5, '...', last];
      if (cur >= last - 3) return [1, '...', last - 4, last - 3, last - 2, last - 1, last];
      return [1, '...', cur - 1, cur, cur + 1, '...', last];
    }
  },
  async mounted() { await this.loadData(); },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [invRes, statusRes] = await Promise.all([
          subscriptionApi.getInvoices(),
          subscriptionApi.getStatus()
        ]);
        this.invoices = invRes.data.data || [];
        this.pagination = invRes.data.meta || this.pagination;
        this.subscription = statusRes.data.data?.subscription;
      } catch (e) {
        console.error('Failed to load billing data:', e);
        this.$toast?.error?.('Failed to load billing data');
      } finally { this.loading = false; }
    },
    async loadInvoices(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      try {
        const res = await subscriptionApi.getInvoices({ page });
        this.invoices = res.data.data || [];
        this.pagination = res.data.meta || this.pagination;
      } catch (e) { console.error(e); }
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatCurrency(v) {
      if (v === null || v === undefined) return '0.00';
      return parseFloat(v).toFixed(2);
    },
    capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; },
    downloadInvoice() { this.$toast?.info?.('Invoice download will be available soon.'); }
  }
};
</script>

<style>
/* ── Page ─────────────────────────────────────────────────── */
.gl-page { padding: 28px 32px 48px; min-height: 100vh; background: #f5f6fa; }
body.dark-only .gl-page { background: #0f1923; }

/* ── Header ──────────────────────────────────────────────── */
.gl-page-header { margin-bottom: 28px; }
.gl-page-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; margin-bottom: 6px; }
.gl-breadcrumb-link { color: #64748b; text-decoration: none; }
.gl-breadcrumb-link:hover { color: #900C27; }
.gl-breadcrumb-sep { color: #cbd5e1; }
.gl-breadcrumb-cur { color: #94a3b8; }
.gl-page-title { font-size: 26px; font-weight: 700; color: #1e293b; margin: 0 0 4px; letter-spacing: -0.3px; }
body.dark-only .gl-page-title { color: #f1f5f9; }
.gl-page-sub { font-size: 13px; color: #94a3b8; margin: 0; }

.gl-btn-contact {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; background: #900C27; color: #fff;
  border-radius: 10px; font-size: 13px; font-weight: 500;
  text-decoration: none; transition: background .18s; white-space: nowrap;
}
.gl-btn-contact:hover { background: #1a4a40; color: #fff; }

/* ── Stats ───────────────────────────────────────────────── */
.gl-stats-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
@media (max-width: 768px) { .gl-stats-strip { grid-template-columns: 1fr; } }

.gl-stat-card {
  background: #fff; border-radius: 14px; padding: 20px 22px;
  display: flex; align-items: center; gap: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06); border: 1px solid #f0f2f5;
}
body.dark-only .gl-stat-card { background: #1a2535; border-color: #243040; box-shadow: none; }

.gl-stat-icon {
  width: 44px; height: 44px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-muted { background: #f1f5f9; color: #94a3b8; }

.gl-stat-label { font-size: 11px; font-weight: 500; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
.gl-stat-value { font-size: 18px; font-weight: 700; color: #1e293b; }
body.dark-only .gl-stat-value { color: #f1f5f9; }
.gl-val-teal   { color: #900C27 !important; }
.gl-val-muted  { color: #94a3b8 !important; }

/* ── Table card ──────────────────────────────────────────── */
.gl-table-card {
  background: #fff; border-radius: 16px; border: 1px solid #f0f2f5;
  box-shadow: 0 1px 4px rgba(0,0,0,.06); overflow: hidden; margin-bottom: 20px;
}
body.dark-only .gl-table-card { background: #1a2535; border-color: #243040; box-shadow: none; }

.gl-table-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; padding: 22px 24px 18px; border-bottom: 1px solid #f0f2f5; flex-wrap: wrap;
}
body.dark-only .gl-table-header { border-color: #243040; }

.gl-section-title { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 2px; }
body.dark-only .gl-section-title { color: #f1f5f9; }
.gl-section-sub { font-size: 12px; color: #94a3b8; margin: 0; }

.gl-table-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.gl-search-box {
  display: flex; align-items: center; gap: 8px;
  background: #f8fafc; border: 1px solid #e8ecf0; border-radius: 9px;
  padding: 7px 12px; color: #94a3b8;
}
body.dark-only .gl-search-box { background: #0f1923; border-color: #243040; }
.gl-search-input { border: none; background: transparent; outline: none; font-size: 13px; color: #334155; width: 160px; }
body.dark-only .gl-search-input { color: #cbd5e1; }
.gl-search-input::placeholder { color: #94a3b8; }

.gl-select-wrap { position: relative; }
.gl-filter-select {
  appearance: none; padding: 8px 32px 8px 12px;
  border: 1px solid #e8ecf0; border-radius: 9px;
  background: #f8fafc; font-size: 13px; color: #334155;
  cursor: pointer; outline: none; font-family: inherit;
}
body.dark-only .gl-filter-select { background: #0f1923; border-color: #243040; color: #cbd5e1; }
.gl-select-chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #94a3b8; }

/* ── Invoice table ───────────────────────────────────────── */
.gl-inv-thead {
  display: grid;
  grid-template-columns: 160px 100px 1fr 180px 100px 44px;
  column-gap: 12px;
  padding: 10px 24px;
  background: #f8fafc; border-bottom: 1px solid #f0f2f5;
  font-size: 11px; font-weight: 600; color: #94a3b8;
  text-transform: uppercase; letter-spacing: .5px;
}
body.dark-only .gl-inv-thead { background: #0f1923; border-color: #1e2d3d; }
.gl-col-right { text-align: right; }

.gl-inv-row {
  display: grid;
  grid-template-columns: 160px 100px 1fr 180px 100px 44px;
  column-gap: 12px;
  align-items: center;
  padding: 0 24px;
  border-bottom: 1px solid #f8fafc;
  border-left: 3px solid transparent;
  transition: background .12s;
  min-height: 60px;
}
body.dark-only .gl-inv-row { border-bottom-color: #1a2535; }
.gl-inv-row:last-child { border-bottom: none; }
.gl-inv-row:hover { background: #f8fafc; }
body.dark-only .gl-inv-row:hover { background: #0f1923; }

/* Status-coloured left borders */
.gl-row-paid      { border-left-color: #16a34a; }
.gl-row-pending   { border-left-color: #f59e0b; }
.gl-row-overdue   { border-left-color: #dc2626; }
.gl-row-cancelled { border-left-color: #94a3b8; }

.gl-inv-row-skel { border-left: none; min-height: 60px; gap: 0; padding: 14px 24px; }

.gl-inv-num {
  font-size: 12.5px; font-weight: 600; color: #900C27;
  font-family: 'Courier New', monospace; letter-spacing: -.2px;
  display: block; text-align: center;
}
body.dark-only .gl-inv-num { color: #2d8474; }
.gl-inv-date { font-size: 12px; color: #64748b; display: block; text-align: center; }
body.dark-only .gl-inv-date { color: #94a3b8; }
.gl-inv-desc { font-size: 13px; color: #334155; }
body.dark-only .gl-inv-desc { color: #cbd5e1; }

.gl-inv-amount { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.gl-inv-total { font-size: 14px; font-weight: 700; color: #1e293b; }
body.dark-only .gl-inv-total { color: #f1f5f9; }
.gl-inv-breakdown { font-size: 11px; color: #94a3b8; white-space: nowrap; }

.gl-inv-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }

/* Status badges */
.gl-badge {
  display: inline-block; padding: 4px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.gl-badge-paid      { background: rgba(22,163,74,.1);   color: #16a34a; }
.gl-badge-pending   { background: rgba(245,158,11,.1);  color: #d97706; }
.gl-badge-overdue   { background: rgba(220,38,38,.1);   color: #dc2626; }
.gl-badge-cancelled { background: rgba(100,116,139,.1); color: #64748b; }
body.dark-only .gl-badge-paid      { background: rgba(22,163,74,.18); }
body.dark-only .gl-badge-pending   { background: rgba(245,158,11,.18); }
body.dark-only .gl-badge-overdue   { background: rgba(220,38,38,.18); }
body.dark-only .gl-badge-cancelled { background: rgba(100,116,139,.18); }

/* Download button — appears on row hover */
.gl-dl-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px;
  border: 1px solid #e8ecf0; background: #f8fafc;
  color: #94a3b8; cursor: pointer; transition: all .15s;
}
.gl-dl-btn:hover { background: #900C27; border-color: #900C27; color: #fff; }
body.dark-only .gl-dl-btn { background: #243040; border-color: #2d3e52; color: #94a3b8; }

/* ── Empty state ─────────────────────────────────────────── */
.gl-empty { padding: 60px 24px; text-align: center; }
.gl-empty-icon {
  width: 56px; height: 56px; border-radius: 16px;
  background: #f1f5f9; color: #94a3b8;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 14px;
}
body.dark-only .gl-empty-icon { background: #1e2d3d; }
.gl-empty-title { font-size: 15px; font-weight: 600; color: #334155; margin: 0 0 6px; }
body.dark-only .gl-empty-title { color: #cbd5e1; }
.gl-empty-sub { font-size: 13px; color: #94a3b8; margin: 0 auto; max-width: 340px; }

/* ── Pagination ──────────────────────────────────────────── */
.gl-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 6px; padding: 20px 24px; border-top: 1px solid #f0f2f5;
}
body.dark-only .gl-pagination { border-color: #243040; }
.gl-pg-btn {
  min-width: 34px; height: 34px; padding: 0 8px;
  border-radius: 9px; border: 1px solid #e8ecf0; background: #fff;
  font-size: 13px; color: #64748b; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all .15s;
}
.gl-pg-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pg-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pg-btn.gl-pg-active { background: #900C27; border-color: #900C27; color: #fff; font-weight: 600; }
body.dark-only .gl-pg-btn { background: #1a2535; border-color: #243040; color: #94a3b8; }
.gl-pg-ellipsis { color: #94a3b8; font-size: 13px; padding: 0 4px; }

/* ── Payment method card ─────────────────────────────────── */
.gl-payment-card {
  background: #fff; border-radius: 16px; border: 1px solid #f0f2f5;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  padding: 22px 24px; display: flex; align-items: center;
  justify-content: space-between; gap: 20px; flex-wrap: wrap;
}
body.dark-only .gl-payment-card { background: #1a2535; border-color: #243040; box-shadow: none; }

.gl-payment-left { display: flex; align-items: center; gap: 16px; }
.gl-payment-icon {
  width: 44px; height: 44px; border-radius: 11px;
  background: rgba(144,12,39,.1); color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-payment-title { font-size: 15px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
body.dark-only .gl-payment-title { color: #f1f5f9; }
.gl-payment-sub { font-size: 13px; color: #64748b; margin: 0; max-width: 460px; line-height: 1.5; }
body.dark-only .gl-payment-sub { color: #94a3b8; }

.gl-btn-contact-outline {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; background: transparent; color: #900C27;
  border: 1.5px solid #900C27; border-radius: 10px; font-size: 13px;
  font-weight: 500; text-decoration: none; transition: all .18s; white-space: nowrap;
}
.gl-btn-contact-outline:hover { background: #900C27; color: #fff; }
body.dark-only .gl-btn-contact-outline { border-color: #2d8474; color: #2d8474; }
body.dark-only .gl-btn-contact-outline:hover { background: #900C27; color: #fff; border-color: #900C27; }

/* ── Shimmer ─────────────────────────────────────────────── */
.gl-shimmer {
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%; animation: gl-shimmer-anim 1.4s infinite;
}
body.dark-only .gl-shimmer {
  background: linear-gradient(90deg, #1e2d3d 25%, #243040 50%, #1e2d3d 75%);
  background-size: 200% 100%;
}
@keyframes gl-shimmer-anim { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Mobile ──────────────────────────────────────────────── */
@media (max-width: 767px) {
  /* Page */
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 16px; }
  .gl-breadcrumb { display: none; }
  .gl-page-title { font-size: 20px; }
  .gl-page-sub { font-size: 12px; }

  /* Header row: stack title + button */
  .gl-page-title-row { flex-direction: column; align-items: stretch; gap: 12px; }
  .gl-btn-contact { justify-content: center; }

  /* Stats strip: all cards full-width */
  .gl-stats-strip { grid-template-columns: 1fr; gap: 10px; margin-bottom: 16px; }
  .gl-stat-card { padding: 14px 14px; gap: 12px; }
  .gl-stat-icon { width: 38px; height: 38px; border-radius: 10px; }
  .gl-stat-value { font-size: 16px; }

  /* Table card header: stack controls below title */
  .gl-table-header { padding: 14px 16px 12px; flex-direction: column; align-items: stretch; gap: 10px; }
  .gl-table-controls { flex-direction: column; gap: 8px; }
  .gl-search-box { width: 100%; box-sizing: border-box; }
  .gl-search-input { flex: 1; width: auto; }
  .gl-select-wrap { width: 100%; }
  .gl-filter-select { width: 100%; box-sizing: border-box; }

  /* Invoice table: card layout (no horizontal scroll) */
  .gl-inv-wrap { overflow-x: visible; }
  .gl-inv-thead { display: none; }

  .gl-inv-row {
    min-width: 0;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "num    badge"
      "desc   amount"
      "date   dl";
    column-gap: 10px;
    row-gap: 4px;
    padding: 14px 16px;
    min-height: unset;
  }
  .gl-inv-num    { grid-area: num;    text-align: left; font-size: 12px; }
  .gl-inv-date   { grid-area: date;   align-self: end; text-align: left; }
  .gl-inv-desc   { grid-area: desc;   font-size: 12px; color: #64748b; white-space: normal; }
  .gl-inv-amount { grid-area: amount; align-items: flex-end; }
  .gl-inv-total  { font-size: 14px; }
  .gl-inv-breakdown { display: none; }
  .gl-badge      { grid-area: badge;  align-self: start; }
  .gl-dl-btn     { grid-area: dl;     justify-self: end; align-self: end; }

  /* Skeleton: simple stacked on mobile */
  .gl-inv-row-skel { min-width: 0; display: flex; flex-direction: column; gap: 8px; padding: 14px 16px; }

  /* Pagination */
  .gl-pagination { padding: 14px 16px; gap: 4px; }
  .gl-pg-btn { min-width: 32px; height: 32px; font-size: 12px; }

  /* Payment method card */
  .gl-payment-card { flex-direction: column; align-items: stretch; padding: 16px; gap: 14px; }
  .gl-payment-left { gap: 12px; align-items: flex-start; }
  .gl-payment-sub { font-size: 12.5px; }
  .gl-btn-contact-outline { text-align: center; justify-content: center; }
}
</style>
